import { useMemo, useState } from 'react';
import bottlesIcon from '../../../images/frontpage/bottles.svg';
import storeIcon from '../../../images/frontpage/frontpage-store.svg';
import { getStoreDetails } from '../../api/store';
import type { StoreWithOpeningHours } from '../../model/types';
import ClientReactRoot from '../../utils/ClientReactRoot';
import getFormattedDate from '../../utils/getFormattedDate';
import useOnMount from '../../utils/useOnMount';
import { ScopeDataAttribute } from '../infrastructure/DataAttributeContext';
import type { ProductLaunchItemProps } from '../page/ListPage/ProductLaunchListPageContent';
import type { KeyedArray } from '../types';
import * as style from './HomePageCards.module.css';
import type { OpeningHoursProps } from './OpeningHours';
import OpeningHours from './OpeningHours';

export type NextLaunchProps = ProductLaunchItemProps & { parent: { path: string } };

interface Props {
    openingHours: KeyedArray<OpeningHoursProps>;
    nextLaunch: NextLaunchProps;
}

export default function HomePageCards({ openingHours, nextLaunch }: Props) {
    return (
        <div className={style.cards}>
            <OpeningHoursCard openingHours={openingHours} />
            <NextLaunchCard {...nextLaunch} />
        </div>
    );
}

function OpeningHoursCard({ openingHours }: { openingHours: KeyedArray<OpeningHoursProps> }) {
    return (
        <ClientReactRoot className={style.card} data-track-link-click="åpningstider">
            <DynamicOpeningHoursCard openingHours={openingHours} />
        </ClientReactRoot>
    );
}

export function DynamicOpeningHoursCard({ openingHours }: { openingHours: KeyedArray<OpeningHoursProps> }) {
    const [data, setData] = useState<null | StoreWithOpeningHours>(null);

    useOnMount(async ({ user }) => {
        const id = user?.favouriteStore?.name;
        if (!id) return;
        const response = await getStoreDetails(id);
        setData(response);
    });

    return (
        <>
            <img src={storeIcon} alt="Ikon for butikker & åpningstider" />
            <div className={style.content}>
                <h2>Butikker & åpningstider</h2>
                {data ? (
                    <FavouriteStoreOpeningHours {...data} />
                ) : (
                    <ScopeDataAttribute path="openingHours">
                        {openingHours
                            ?.filter((p) => p._id)
                            .map(({ _key, ...props }) => (
                                <ScopeDataAttribute key={_key} _key={_key}>
                                    <OpeningHours {...props} />
                                </ScopeDataAttribute>
                            ))}
                    </ScopeDataAttribute>
                )}
            </div>
            <div className={style.btns}>
                <a className="btn-secondary" href="/finn-butikk">
                    Alle butikker og åpningstider
                </a>
                {data && (
                    <a className="btn-secondary" href={`/search?q=%3Arelevance%3AavailableInStores%3A${data.name}`}>
                        Se vareutvalg
                    </a>
                )}
            </div>
        </>
    );
}

function FavouriteStoreOpeningHours(props: StoreWithOpeningHours) {
    const { weekdays, deviatingOpeningHours } = useMemo(() => {
        const weekdays = props.openingHours.weekDayOpeningList;

        const deviatingOpeningHours = props.openingHours.specialOpeningList;

        return { weekdays, deviatingOpeningHours };
    }, [props]);

    return (
        <>
            <div className={style.launch}>
                <h3>
                    <a href={`/butikk/${props.name}`}>{props.displayName}</a>
                </h3>

                <ul>
                    {weekdays.map((w) => (
                        <li key={w.day.formattedValue}>
                            <span>{w.day.formattedValue}</span>
                            <time dateTime={w.hours.readableValue}>{w.hours.formattedValue}</time>
                        </li>
                    ))}
                </ul>
            </div>

            {!!deviatingOpeningHours?.length && (
                <div className={style.launch}>
                    <h3>Avvikende åpningstider</h3>
                    <ul>
                        {deviatingOpeningHours.map((w) => (
                            <li key={w.day.formattedValue}>
                                <span>{w.day.formattedValue}</span>
                                <time dateTime={w.hours.readableValue}>{w.hours.formattedValue}</time>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
}

function NextLaunchCard({
    path,
    shortTitle,
    isOnlineLaunchEnabled,
    isStoreLaunchEnabled,
    storeLaunchDate,
    onlineLaunchDate,
    parent,
}: NextLaunchProps & { parent: { path: string } }) {
    return (
        <div className={style.card} data-track-link-click="neste-lansering">
            <img src={bottlesIcon} alt="Ikon for lanseringer" />
            <div className={style.content}>
                <h2>Lanseringer</h2>
                <div className={style.launch}>
                    <h3>
                        <a href={path}>Neste: {shortTitle}</a>
                    </h3>

                    <ul>
                        {isStoreLaunchEnabled && (
                            <li>
                                <span>Butikk</span> <time dateTime={storeLaunchDate}>{getFormattedDate(storeLaunchDate, 'date_same_year')}</time>
                            </li>
                        )}
                        {isOnlineLaunchEnabled && (
                            <li>
                                <span>Nettbutikk</span>{' '}
                                <time dateTime={onlineLaunchDate}>{getFormattedDate(onlineLaunchDate, 'date_same_year')}</time>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            <div className={style.btns}>
                <a className="btn-secondary" href={parent?.path}>
                    Alle lanseringer
                </a>
            </div>
        </div>
    );
}
